import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  ReportStructureDTOSchema,
  type CreateReportStructureInput,
  type UpdateReportStructureInput,
} from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const ListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(ReportStructureDTOSchema),
});

export function useReportStructures(options?: { enabled?: boolean }) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["report-structures"],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/report-structures",
      });
      return ListResponseSchema.parse(json).data;
    },
    enabled: options?.enabled ?? true,
    // Structures change about as often as templates do.
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveReportStructure(id?: string | null) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: CreateReportStructureInput | UpdateReportStructureInput) =>
      id
        ? backend({
            method: "PATCH",
            url: `/api/v1/report-structures/${id}`,
            data,
          })
        : backend({ method: "POST", url: "/api/v1/report-structures", data }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report-structures"] });
      toast.success(t("reportStructures.saved"));
    },
    onError: () => toast.error(t("reportStructures.saveError")),
  });
}

export function useDeleteReportStructure() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/report-structures/${id}` }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report-structures"] });
      toast.success(t("reportStructures.deleted"));
    },
    // The API refuses to delete the default structure (409) — surface why.
    onError: () => toast.error(t("reportStructures.deleteError")),
  });
}
